import { Injectable } from '@angular/core';

export interface CommentWrapperContext {
  hasActivePage: boolean;
  commentsCollapsed: boolean;
  isNavigatingToProjectDashboard: boolean;
  isCommentLoading: boolean;
  isCommentsPaneAvailable: boolean;
  isCommentExpanded: boolean;
  isPlatformCommentsChatShowing: boolean;
  isMediaPreviewCommentsChatShowing: boolean;
  /** Set when theme has registered `mediaPreviewCommentsChat` (stays true across asset switches). */
  hasMediaPreviewCommentsTemplate: boolean;
  isCommentPanelFullSize: boolean;
  isTransitioningFullSize: boolean;
  isResizing: boolean;
  isCommentPluginReady: boolean;
  commentPanelWidth: number;
}

@Injectable({
  providedIn: 'root',
})
export class CommentComponentViewService {
  getCommentWrapperStyle(context: CommentWrapperContext): { [key: string]: string } {
    const {
      hasActivePage,
      isCommentExpanded,
      isPlatformCommentsChatShowing,
      isMediaPreviewCommentsChatShowing,
      isNavigatingToProjectDashboard,
      isCommentLoading,
    } = context;

    const isLoadingComponent =
      isNavigatingToProjectDashboard ||
      (!isPlatformCommentsChatShowing && !isMediaPreviewCommentsChatShowing) ||
      isCommentLoading;

    if (isLoadingComponent) {
      return { 'background-color': '#ffffff' };
    }

    // When platform comment chat is showing, ignore commentsCollapsed and use isCommentExpanded
    const shouldUseTransparentBackground =
      (!hasActivePage || !isCommentExpanded) && !isNavigatingToProjectDashboard && !isCommentLoading;

    if (shouldUseTransparentBackground) {
      return {};
    }

    return { 'background-color': '#ffffff' };
  }

  getCommentWrapperClassMap(context: CommentWrapperContext): Record<string, boolean> {
    const {
      hasActivePage,
      commentsCollapsed,
      isNavigatingToProjectDashboard,
      isCommentLoading,
      isCommentsPaneAvailable,
      isCommentExpanded,
      isPlatformCommentsChatShowing,
      isMediaPreviewCommentsChatShowing,
      hasMediaPreviewCommentsTemplate,
      isCommentPanelFullSize,
      isTransitioningFullSize,
      isResizing,
    } = context;

    const shouldShowCommentPanel =
      ((hasActivePage || isMediaPreviewCommentsChatShowing) &&
        (isPlatformCommentsChatShowing
          ? isCommentExpanded && isCommentsPaneAvailable
          : !commentsCollapsed || (isCommentsPaneAvailable && isCommentExpanded))) ||
      isNavigatingToProjectDashboard ||
      isCommentLoading;

    const hasExpandedComment = shouldShowCommentPanel;

    const isCollapsedComment = !shouldShowCommentPanel;

    const isLoadingComponent =
      (!isPlatformCommentsChatShowing && !isMediaPreviewCommentsChatShowing) ||
      isNavigatingToProjectDashboard ||
      isCommentLoading ||
      (!hasMediaPreviewCommentsTemplate && isMediaPreviewCommentsChatShowing);

    const hasRightMargin =
      (isPlatformCommentsChatShowing && !isMediaPreviewCommentsChatShowing) || isMediaPreviewCommentsChatShowing;

    return {
      'me-1': hasRightMargin,
      fullsize: isCommentPanelFullSize,
      'transitioning-fullsize': isTransitioningFullSize,
      'comment-expanded': hasExpandedComment,
      'comment-collapsed': isCollapsedComment,
      'loading-component': isLoadingComponent,
      resizing: isResizing,
    };
  }

  getCommentWrapperWidth(context: CommentWrapperContext): number {
    const {
      hasActivePage,
      commentsCollapsed,
      isNavigatingToProjectDashboard,
      isCommentLoading,
      isCommentsPaneAvailable,
      isCommentExpanded,
      isPlatformCommentsChatShowing,
      isMediaPreviewCommentsChatShowing,
      commentPanelWidth,
    } = context;

    const isPlatformPanelExpanded = isPlatformCommentsChatShowing && isCommentsPaneAvailable && isCommentExpanded;
    const isMediaPreviewPanelExpanded = isMediaPreviewCommentsChatShowing && !commentsCollapsed;

    if (isMediaPreviewCommentsChatShowing && !commentsCollapsed) {
      return commentPanelWidth;
    }

    const shouldShowCommentPanelWidth =
      ((hasActivePage || isMediaPreviewCommentsChatShowing) &&
        (isPlatformPanelExpanded || isMediaPreviewPanelExpanded)) ||
      ((isNavigatingToProjectDashboard || isCommentLoading) &&
        (isPlatformPanelExpanded || isMediaPreviewPanelExpanded));

    return shouldShowCommentPanelWidth ? commentPanelWidth : 0;
  }
}
